import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {SubsurfaceScatteringSockets} from "#material/nodes/shader/subsurface-scattering/definition"
import {subsurfaceScatteringDefinition} from "#material/nodes/shader/subsurface-scattering/definition"
import type {ThreeEvaluator, CyclesEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {SubsurfaceScatteringNode} from "#material/cycles/types"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import {cyclesFalloff} from "#material/nodes/shader/subsurface-scattering/helpers"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"
import {threeConvert, threeRGBColor, threeValue, threeVec3} from "#material/three/three-utils"

@registerMaterialGraphNode
export class SubsurfaceScattering extends MaterialNode<SubsurfaceScatteringSockets> {
    public get definition() {
        return subsurfaceScatteringDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<SubsurfaceScatteringSockets>> => {
        const material = new ThreeColormassMaterial()
        const {color, normal, radius, scale} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            normal: this.parameters.inputs.normal,
            radius: this.parameters.inputs.radius,
            scale: this.parameters.inputs.scale,
        })
        const settings = this.parameters.settings

        const colorValue = color?.toColor() ?? threeConvert(settings.color, threeRGBColor)

        material.colorNode = colorValue ?? material.colorNode
        material.roughnessNode = threeValue(1)
        material.metalnessNode = threeValue(0)
        material.specularIntensityNode = threeValue(0)
        material.normalNode =
            normal?.toVec3() ?? threeConvert(settings.normal, threeVec3, (value) => value.x !== 0 || value.y !== 0 || value.z !== 0) ?? material.normalNode

        const radiusValue = radius?.toVec3() ?? threeConvert(settings.radius, threeVec3, (value) => value.x > 0 || value.y > 0 || value.z > 0)
        if (radiusValue) {
            const scaleValue = scale?.toFloat() ?? threeConvert(settings.scale, threeValue) ?? threeValue(1)
            // three has no BSSRDF, so the scattering distance becomes the depth light travels before it is absorbed
            material.transparent = true
            material.transmissionNode = colorValue ?? threeValue(1)
            material.transmission = 0.01
            material.thicknessNode = radiusValue.x.add(radiusValue.y).add(radiusValue.z).div(3).mul(scaleValue)
        }

        return {bssrdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<SubsurfaceScatteringSockets>> => {
        const bssrdf: SubsurfaceScatteringNode = {
            type: "subsurface_scattering",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                normal: this.parameters.inputs.normal,
                scale: this.parameters.inputs.scale,
                radius: this.parameters.inputs.radius,
                subsurface_ior: this.parameters.inputs.ior,
                subsurface_roughness: this.parameters.inputs.roughness,
                subsurface_anisotropy: this.parameters.inputs.anisotropy,
                surface_mix_weight: undefined,
            }),
            parameters: {
                color: cyclesColor(this.parameters.settings.color),
                normal: cyclesVec3(this.parameters.settings.normal),
                scale: this.parameters.settings.scale,
                radius: cyclesVec3(this.parameters.settings.radius),
                subsurface_ior: this.parameters.settings.ior,
                subsurface_roughness: this.parameters.settings.roughness,
                subsurface_anisotropy: this.parameters.settings.anisotropy,
                surface_mix_weight: undefined,
                method: cyclesFalloff(this.parameters.settings.falloff),
            },
            resources: {},
        }

        return {bssrdf: [bssrdf, "bssrdf"]}
    }
}
